import { FunctionComponent, MouseEventHandler, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { TrackingFeatureTypes, trackFeature } from '@/utils/adobeLaunch';
import { t } from '@/utils/i18n';
import { useCanPrintOnView } from '@/hooks/useCanPrintOnView';
import { addPlaceholderTextConfigurationAction } from '@/redux/configurations/actions/addPlaceholderTextConfigurationAction';
import { addTextConfigurationAction } from '@/redux/configurations/actions/addTextConfigurationAction';
import { getFeatures } from '@/redux/core/getters/getFeatures';
import { StoreDispatch } from '@/redux/types';
import { Design } from '@/components/common/icons/Design';
import { Redo } from '@/components/common/icons/Redo';
import { Shirt } from '@/components/common/icons/Shirt';
import { Text } from '@/components/common/icons/Text';
import { Undo } from '@/components/common/icons/Undo';
import { Upload } from '@/components/common/icons/Upload';
import {
  redoAction,
  undoAction,
  useUndoRedo,
} from '@/components/draft/draftState/history/draftHistory';
import { MenuButton } from '../common/Menu';
import './DesktopMenu.scss';

const undoRedoIconClassName = { className: 'menu__undo-redo__icon' };

export const DesktopMenu: FunctionComponent = () => {
  const {
    showProductTypeGallery,
    showDesignGallery,
    showAddText,
    showDesignUpload,
    showAddPlaceholderText,
  } = useSelector(getFeatures);
  const dispatch = useDispatch<StoreDispatch>();
  const [canPrintOnView, canPrintTextOnView, showDisabledHint] = useCanPrintOnView();
  const [canRedo, canUndo] = useUndoRedo();

  const handleUndo = useCallback(() => {
    trackFeature('undo');
    dispatch(undoAction);
  }, [dispatch]);

  const handleRedo = useCallback(() => {
    trackFeature('redo');
    dispatch(redoAction);
  }, [dispatch]);

  const handleProductClick: MouseEventHandler = () => {
    trackFeature('menu-product', undefined, true, TrackingFeatureTypes.ACTION);
  };

  const handleDesignClick: MouseEventHandler = () => {
    trackFeature('menu-design');
  };

  const handleTextClick: MouseEventHandler = () => {
    trackFeature('menu-text');
    dispatch(addTextConfigurationAction(t('general.defaultText')));
  };

  const handlePlaceholderTextClick: MouseEventHandler = () => {
    dispatch(addPlaceholderTextConfigurationAction);
  };

  const handleUploadClick: MouseEventHandler = () => {
    trackFeature('menu-upload');
  };

  return (
    <div id="menu" className="menu">
      <div className="menu__inner">
        {showProductTypeGallery && (
          <MenuButton
            to="/productTypes"
            onClick={handleProductClick}
            text={t('menu.product')}
            icon={Shirt}
          />
        )}
        {showDesignGallery && (
          <div className="menu__buttonWithTooltip">
            <MenuButton
              to="/designs"
              onClick={handleDesignClick}
              text={t('menu.design')}
              icon={Design}
              disabled={!canPrintOnView}
              onDisabledClick={showDisabledHint}
            />
          </div>
        )}
        {showAddText && (
          <MenuButton
            onClick={handleTextClick}
            text={t('menu.text')}
            icon={Text}
            disabled={!canPrintTextOnView}
            onDisabledClick={showDisabledHint}
          />
        )}
        {showDesignUpload && (
          <MenuButton
            to="/upload"
            onClick={handleUploadClick}
            text={t('menu.upload')}
            icon={Upload}
            disabled={!canPrintOnView}
            onDisabledClick={showDisabledHint}
          />
        )}
        {showAddPlaceholderText && (
          <MenuButton
            onClick={handlePlaceholderTextClick}
            text="Text Placeholder"
            icon={Text}
            disabled={!canPrintOnView}
            onDisabledClick={showDisabledHint}
          />
        )}
      </div>

      <div className="menu__undo-redo">
        <MenuButton
          onClick={handleUndo}
          disabled={!canUndo}
          icon={Undo}
          iconProps={undoRedoIconClassName}
          text={t('general.undo')}
          className="menu__undo-button"
        />

        <MenuButton
          onClick={handleRedo}
          disabled={!canRedo}
          icon={Redo}
          iconProps={undoRedoIconClassName}
          text={t('general.redo')}
          className="menu__redo-button"
        />
      </div>
    </div>
  );
};
